import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ==================== Types ====================

interface SerializedEfficiencyState {
  recentItems: RecentItem[]
  favorites: FavoriteItem[]
  drafts: [string, DraftData][]
  preferences: EfficiencyPreferences | null
  lastSyncedAt: string | null
}

export type EntityType = 'project' | 'issue' | 'employee' | 'team'

export interface RecentItem {
  type: EntityType
  id: string
  name: string
  path: string
  visitedAt: string
  metadata?: Record<string, unknown>
}

export interface FavoriteItem {
  id: string
  type: EntityType
  entityId: string
  name: string
  path: string
  order: number
  addedAt: string
}

export interface DraftData {
  formType: string
  data: Record<string, unknown>
  savedAt: string
  expiresAt: string
}

export interface EfficiencyPreferences {
  enableQuickEntry: boolean
  enableSuggestions: boolean
  enableShortcuts: boolean
  enableDraftAutoSave: boolean
  enableNotificationDigest: boolean
  timelogReminderTime: string | null
  defaultTimelogHours: number | null
  dailyTimelogGoal: number | null
  doNotDisturb: boolean
  customShortcuts: Record<string, string> | null
}

// ==================== Constants ====================

export const MAX_RECENT_ITEMS = 5
const DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

// ==================== State Interface ====================

interface EfficiencyState {
  // Recent items (local cache)
  recentItems: RecentItem[]
  addRecentItem: (item: Omit<RecentItem, 'visitedAt'>) => void
  clearRecentItems: () => void

  // Favorites (local cache)
  favorites: FavoriteItem[]
  setFavorites: (items: FavoriteItem[]) => void
  addFavoriteLocal: (item: FavoriteItem) => void
  removeFavoriteLocal: (id: string) => void
  reorderFavoritesLocal: (items: FavoriteItem[]) => void

  // Drafts (local cache with auto-save)
  drafts: Map<string, DraftData>
  saveDraftLocal: (formType: string, data: Record<string, unknown>) => void
  getDraftLocal: (formType: string) => DraftData | null
  clearDraftLocal: (formType: string) => void
  clearExpiredDrafts: () => void

  // Preferences (local cache)
  preferences: EfficiencyPreferences | null
  setPreferences: (prefs: EfficiencyPreferences) => void

  // Sync status
  lastSyncedAt: string | null
  setLastSyncedAt: (time: string) => void
}

// ==================== Default Preferences ====================

export const defaultPreferences: EfficiencyPreferences = {
  enableQuickEntry: true,
  enableSuggestions: true,
  enableShortcuts: true,
  enableDraftAutoSave: true,
  enableNotificationDigest: true,
  timelogReminderTime: '18:00',
  defaultTimelogHours: 2.0,
  dailyTimelogGoal: 8.0,
  doNotDisturb: false,
  customShortcuts: null,
}

// ==================== Store ====================

export const useEfficiencyStore = create<EfficiencyState>()(
  persist(
    (set, get) => ({
      // Recent items
      recentItems: [],
      addRecentItem: (item) => {
        const { recentItems } = get()
        const now = new Date().toISOString()

        // Remove existing item with same type and id
        const filtered = recentItems.filter(
          (r) => !(r.type === item.type && r.id === item.id)
        )

        // Add new item at the beginning
        const newItem: RecentItem = { ...item, visitedAt: now }
        const newItems = [newItem, ...filtered].slice(0, MAX_RECENT_ITEMS)

        set({ recentItems: newItems })
      },
      clearRecentItems: () => set({ recentItems: [] }),

      // Favorites
      favorites: [],
      setFavorites: (items) => set({ favorites: items }),
      addFavoriteLocal: (item) => {
        const { favorites } = get()
        // Check if already exists
        if (favorites.some((f) => f.id === item.id)) {
          return
        }
        set({ favorites: [...favorites, item] })
      },
      removeFavoriteLocal: (id) => {
        const { favorites } = get()
        set({ favorites: favorites.filter((f) => f.id !== id) })
      },
      reorderFavoritesLocal: (items) => set({ favorites: items }),

      // Drafts
      drafts: new Map(),
      saveDraftLocal: (formType, data) => {
        const { drafts } = get()
        const now = new Date()
        const expiresAt = new Date(now.getTime() + DRAFT_EXPIRY_MS)

        const newDrafts = new Map(drafts)
        newDrafts.set(formType, {
          formType,
          data,
          savedAt: now.toISOString(),
          expiresAt: expiresAt.toISOString(),
        })

        set({ drafts: newDrafts })
      },
      getDraftLocal: (formType) => {
        const { drafts } = get()
        const draft = drafts.get(formType)
        if (!draft) {
          return null
        }

        // Check if expired
        if (new Date(draft.expiresAt) < new Date()) {
          // Remove expired draft
          const newDrafts = new Map(drafts)
          newDrafts.delete(formType)
          set({ drafts: newDrafts })
          return null
        }

        return draft
      },
      clearDraftLocal: (formType) => {
        const { drafts } = get()
        const newDrafts = new Map(drafts)
        newDrafts.delete(formType)
        set({ drafts: newDrafts })
      },
      clearExpiredDrafts: () => {
        const { drafts } = get()
        const now = new Date()
        const newDrafts = new Map<string, DraftData>()

        drafts.forEach((draft, key) => {
          if (new Date(draft.expiresAt) >= now) {
            newDrafts.set(key, draft)
          }
        })

        set({ drafts: newDrafts })
      },

      // Preferences
      preferences: null,
      setPreferences: (prefs) => set({ preferences: prefs }),

      // Sync status
      lastSyncedAt: null,
      setLastSyncedAt: (time) => set({ lastSyncedAt: time }),
    }),
    {
      name: 'efficiency-storage',
      partialize: (state) => ({
        recentItems: state.recentItems,
        favorites: state.favorites,
        drafts: Array.from(state.drafts.entries()),
        preferences: state.preferences,
        lastSyncedAt: state.lastSyncedAt,
      }),
      // Custom serialization for Map
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name)
          if (!str) {
            return null
          }
          try {
            return JSON.parse(str) as { state: SerializedEfficiencyState; version?: number }
          } catch (error) {
            console.error('Failed to parse efficiency storage:', error)
            return null
          }
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      onRehydrateStorage: () => (state) => {
        if (state && state.drafts && Array.isArray(state.drafts)) {
          // Convert Array back to Map during hydration
          state.drafts = new Map(state.drafts)
        }
      },
    }
  )
)
